import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Package, CheckCircle, Truck } from 'lucide-react';
import { getOrder } from '../services/api';

export default function ProductionConfirm() {
  const { orderId } = useParams<{ orderId: string }>();
  const [confirmed, setConfirmed] = useState(false);

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(orderId!),
    enabled: !!orderId,
  });

  const handleConfirm = async () => {
    // In production, this would call an API to confirm
    setConfirmed(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uber-green"></div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-uber-green mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Confirmed!</h1>
          <p className="text-gray-600">
            Thank you for confirming. The driver will arrive shortly to pick up
            the package.
          </p>
        </div>
      </div>
    );
  }

  const latestDelivery = order?.deliveries?.[0];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="max-w-md w-full">
        <div className="card">
          <div className="text-center mb-6">
            <Package className="w-12 h-12 text-uber-green mx-auto mb-2" />
            <h1 className="text-2xl font-bold">Driver En Route</h1>
            <p className="text-gray-600">
              Order {order?.shopifyOrderNumber}
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Truck className="w-6 h-6 text-yellow-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-yellow-800">
                  A driver is on their way!
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Please have the package ready for pickup.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-gray-600">Customer</span>
              <span className="font-medium">{order?.customerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Destination</span>
              <span className="font-medium text-right">
                {order?.deliveryCity}, {order?.deliveryState}
              </span>
            </div>
            {latestDelivery?.driverName && (
              <div className="flex justify-between">
                <span className="text-gray-600">Driver</span>
                <span className="font-medium">{latestDelivery.driverName}</span>
              </div>
            )}
            {latestDelivery?.etaMinutes && (
              <div className="flex justify-between">
                <span className="text-gray-600">ETA</span>
                <span className="font-medium">
                  {latestDelivery.etaMinutes} minutes
                </span>
              </div>
            )}
          </div>

          <button
            onClick={handleConfirm}
            className="btn-primary w-full py-3 text-lg"
          >
            Confirm Package is Ready
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            Please confirm that the package is ready and you are available for
            the driver.
          </p>
        </div>
      </div>
    </div>
  );
}
